import { DOCUMENT } from '@angular/common';
import { Component, Inject, NgZone, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SWIPER_INTERACTION_EMITTER, SwiperInteractionEvent } from '@planone/shared/data-access-swiper';
import { BehaviorSubject } from 'rxjs';
import { SwiperOptions } from 'swiper';

@Component({
  selector: 'planone-swiper-zoom-modal',
  templateUrl: './swiper-zoom-modal.component.html',
  styleUrls: ['./swiper-zoom-modal.component.scss'],
})
export class SwiperZoomModalComponent implements OnInit, OnDestroy {
  initialized = false;

  @ViewChild('swiperSlider') swiper!: any;

  constructor(
    @Inject(SWIPER_INTERACTION_EMITTER) private swiperInteractionEmitter: BehaviorSubject<SwiperInteractionEvent>,
    @Inject(DOCUMENT) private document: Document,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      slides?: QueryList<TemplateRef<never>>;
      titles?: QueryList<TemplateRef<never>>;
      slideCount?: number;
      slideActive?: number;
      swiperOptions: SwiperOptions;
      parent: unknown;
    },
    private zone: NgZone
  ) {
    this.data = {
      ...this.data,
      swiperOptions: {
        ...this.data.swiperOptions,
        effect: 'fade',
        fadeEffect: {
          crossFade: true,
        },
      },
    };
  }

  ngOnInit() {
    Array.prototype.forEach.call(document.getElementsByClassName('mat-toolbar'), (el) => {
      el.classList.add('blurred');
    });

    Array.prototype.forEach.call(document.getElementsByClassName('mat-drawer-content'), (el) => {
      el.classList.add('blurred');
    });
  }

  ngOnDestroy() {
    Array.prototype.forEach.call(document.getElementsByClassName('mat-toolbar'), (el) => {
      el.classList.remove('blurred');
    });

    Array.prototype.forEach.call(document.getElementsByClassName('mat-drawer-content'), (el) => {
      el.classList.remove('blurred');
    });
  }

  sliderInit() {
    this.data.slideCount = this.data.slides?.length ?? 0;

    this.swiper?.swiperRef.slideTo(this.data.slideActive ? this.data.slideActive - 1 : 0);

    this.sliderPreviewDrag();
  }

  sliderChange(swiper: any) {
    const activeIndex = swiper[0].activeIndex;

    this.zone.run(() => {
      this.data.slideActive = activeIndex + 1;

      if (!this.initialized) {
        this.initialized = true;
      }

      this.swiperInteractionEmitter.next({
        type: 'SWIPE_TO',
        index: activeIndex,
      });

      this.document.getElementById('sliderMetaPreviews' + this.data.slideActive)?.scrollIntoView();
    });
  }

  sliderNext() {
    (<any>this.swiper)?.swiperRef.slideNext(100);
    (<any>this.data.parent)?.swiperRef.slideNext(100);
  }

  sliderPrevious() {
    (<any>this.swiper)?.swiperRef.slidePrev(100);
    (<any>this.data.parent)?.swiperRef.slidePrev(100);
  }

  sliderSlide(index: number) {
    (<any>this.swiper)?.swiperRef.slideTo(index);
    (<any>this.data.parent)?.swiperRef.slideTo(index);
  }

  sliderPreviewDrag() {
    const slider = this.document.getElementById('sliderMetaPreviews');

    let isDown = false;
    let startX: number;
    let scrollLeft: number;

    const sliderPreviews = this.document.getElementsByClassName('slider-previews-item');

    Array.prototype.forEach.call(sliderPreviews, (parent) => {
      Array.prototype.forEach.call(parent.children, (child) => {
        child.setAttribute('draggable', false);
      });
    });

    slider?.addEventListener('mousedown', (e) => {
      isDown = true;
      slider.classList.add('active');
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });

    slider?.addEventListener('touchstart', (e) => {
      isDown = true;
      slider.classList.add('active');
      startX = e.touches[0].pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });

    slider?.addEventListener('mouseleave', () => {
      isDown = false;
      slider.classList.remove('active');
    });

    slider?.addEventListener('touchcancel', () => {
      isDown = false;
      slider.classList.remove('active');
    });

    slider?.addEventListener('mouseup', () => {
      isDown = false;
      slider.classList.remove('active');
    });

    slider?.addEventListener('touchend', () => {
      isDown = false;
      slider.classList.remove('active');
    });

    slider?.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 3;
      slider.scrollLeft = scrollLeft - walk;
    });

    slider?.addEventListener('touchmove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.touches[0].pageX - slider.offsetLeft;
      const walk = (x - startX) * 3;
      slider.scrollLeft = scrollLeft - walk;
    });
  }

  identifyByIndex(index: number): string {
    return `${index}`;
  }
}
